import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";
import { movies } from "@/mocks/movies";
import { newsList } from "@/mocks/news";
import MovieCard from "@/components/feature/MovieCard";
import NewsCard from "@/components/feature/NewsCard";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";

const MOVIE_PAGE_SIZE = 8;
const NEWS_PAGE_SIZE = 6;

export default function Search() {
  const [params, setParams] = useSearchParams();
  const q = (params.get("q") || "").trim();
  const [input, setInput] = useState(q);
  const [tab, setTab] = useState<"movie" | "news">("movie");
  const [moviePage, setMoviePage] = useState(1);
  const [newsPage, setNewsPage] = useState(1);

  useEffect(() => {
    setInput(q);
    setMoviePage(1);
    setNewsPage(1);
  }, [q]);

  usePageMeta({
    title: q ? `“${q}”的搜索结果 - 电影天堂` : "搜索 - 电影天堂",
    description: q
      ? `电影天堂关于“${q}”的搜索结果，包含相关电影与影视资讯，方便你快速定位想看的内容。`
      : "在电影天堂搜索电影名称与影视资讯标题，快速找到你想看的内容。",
    keywords: q ? `${q},搜索,电影天堂,电影资源,影视资讯` : "搜索,电影天堂",
  });

  const matchedMovies = useMemo(() => {
    if (!q) return [];
    const kw = q.toLowerCase();
    return movies.filter(
      (m) =>
        m.title.toLowerCase().includes(kw) ||
        m.director.toLowerCase().includes(kw) ||
        m.cast.some((c) => c.toLowerCase().includes(kw)) ||
        m.types.some((t) => t.toLowerCase().includes(kw))
    );
  }, [q]);

  const matchedNews = useMemo(() => {
    if (!q) return [];
    const kw = q.toLowerCase();
    return newsList.filter(
      (n) => n.title.toLowerCase().includes(kw) || n.summary.toLowerCase().includes(kw)
    );
  }, [q]);

  const movieTotalPages = Math.max(1, Math.ceil(matchedMovies.length / MOVIE_PAGE_SIZE));
  const newsTotalPages = Math.max(1, Math.ceil(matchedNews.length / NEWS_PAGE_SIZE));
  const movieItems = matchedMovies.slice((moviePage - 1) * MOVIE_PAGE_SIZE, moviePage * MOVIE_PAGE_SIZE);
  const newsItems = matchedNews.slice((newsPage - 1) * NEWS_PAGE_SIZE, newsPage * NEWS_PAGE_SIZE);

  const hotMovies = [...movies].sort((a, b) => b.heat - a.heat).slice(0, 5);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const next = input.trim();
    if (!next) return;
    setParams({ q: next });
  };

  const noResult = q && matchedMovies.length === 0 && matchedNews.length === 0;

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
      <h1 className="font-heading text-2xl font-black text-foreground-950">
        {q ? "搜索结果" : "搜索影片与资讯"}
      </h1>

      <form
        onSubmit={submit}
        className="mt-5 flex items-center gap-2 rounded-full border border-background-300 bg-background-50 px-4 py-2.5"
      >
        <i className="ri-search-line text-lg text-foreground-500"></i>
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="输入电影名称或资讯关键词"
          className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-500 md:text-base"
        />
        <button
          type="submit"
          className="shrink-0 rounded-full bg-primary-500 px-4 py-1.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 dark:text-foreground-950"
        >
          搜索
        </button>
      </form>

      {q && (
        <p className="mt-4 text-sm text-foreground-600">
          关键词：<span className="font-medium text-primary-600">“{q}”</span>
          ，共找到 {matchedMovies.length} 部电影、{matchedNews.length} 篇资讯
        </p>
      )}

      {!q ? (
        <div className="mt-8">
          <EmptyState
            icon="ri-search-line"
            title="输入关键词开始搜索"
            description="支持按电影名称、导演、主演、类型，以及资讯标题与摘要进行检索。"
            actionLabel="随便看看热门影片"
            onAction={() => setParams({ q: "科幻" })}
          />
        </div>
      ) : noResult ? (
        <div className="mt-8 space-y-8">
          <EmptyState
            icon="ri-emotion-sad-line"
            title="没有找到相关内容"
            description={`换个关键词试试，或从下方热门影片中挑选一部吧。`}
          />
          <section>
            <h2 className="mb-4 font-heading text-lg font-bold text-foreground-950">热门影片推荐</h2>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-5">
              {hotMovies.map((m) => (
                <MovieCard key={m.id} movie={m} />
              ))}
            </div>
          </section>
        </div>
      ) : (
        <>
          {/* 结果切换 */}
          <div className="mt-6 inline-flex items-center gap-1 rounded-full border border-background-200 bg-background-50 p-1">
            <button
              type="button"
              onClick={() => setTab("movie")}
              className={`flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                tab === "movie"
                  ? "bg-primary-500 text-background-50 dark:text-foreground-950"
                  : "text-foreground-600 hover:text-primary-600"
              }`}
            >
              <i className="ri-film-line"></i>
              电影
              <span className="text-xs opacity-80">({matchedMovies.length})</span>
            </button>
            <button
              type="button"
              onClick={() => setTab("news")}
              className={`flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                tab === "news"
                  ? "bg-primary-500 text-background-50 dark:text-foreground-950"
                  : "text-foreground-600 hover:text-primary-600"
              }`}
            >
              <i className="ri-newspaper-line"></i>
              资讯
              <span className="text-xs opacity-80">({matchedNews.length})</span>
            </button>
          </div>

          {tab === "movie" ? (
            <div className="mt-6">
              {movieItems.length === 0 ? (
                <EmptyState
                  icon="ri-film-line"
                  title="没有匹配的电影"
                  description="切换到资讯结果看看，或更换关键词重新搜索。"
                  actionLabel="切换查看资讯"
                  onAction={() => setTab("news")}
                />
              ) : (
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-4">
                  {movieItems.map((m) => (
                    <MovieCard key={m.id} movie={m} />
                  ))}
                </div>
              )}
              {movieTotalPages > 1 && (
                <div className="mt-10">
                  <Pagination page={moviePage} totalPages={movieTotalPages} onChange={setMoviePage} />
                </div>
              )}
            </div>
          ) : (
            <div className="mt-6">
              {newsItems.length === 0 ? (
                <EmptyState
                  icon="ri-newspaper-line"
                  title="没有匹配的资讯"
                  description="切换到电影结果看看，或更换关键词重新搜索。"
                  actionLabel="切换查看电影"
                  onAction={() => setTab("movie")}
                />
              ) : (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {newsItems.map((n) => (
                    <NewsCard key={n.id} item={n} />
                  ))}
                </div>
              )}
              {newsTotalPages > 1 && (
                <div className="mt-10">
                  <Pagination page={newsPage} totalPages={newsTotalPages} onChange={setNewsPage} />
                </div>
              )}
            </div>
          )}

          <div className="mt-10 flex justify-center">
            <Link
              to="/"
              className="inline-flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-600"
            >
              <i className="ri-home-4-line"></i>
              返回首页
            </Link>
          </div>
        </>
      )}
    </div>
  );
}